import format from 'date-fns/format';
import parse from 'date-fns/parse';
import ruLocale from 'date-fns/locale/ru';
import { isToday, isYesterday } from 'date-fns';

import {
  PHONE_MASK,
  FTTB_MASK,
} from 'constants/common';

export function removeNewLines(input) {
  return input.replace(/\n/g, ' ');
}

export function formatPhone(value) {
  if (!value) {
    return '';
  }
  let mask = PHONE_MASK;
  const onlyDigits = value.replace(/\+7|\D/g, '');

  let lastDigit;
  onlyDigits.split('').forEach((digit) => {
    if (mask.indexOf('_') > 0) {
      mask = mask.replace('_', digit);
      lastDigit = digit;
    }
  });

  return mask.slice(0, mask.lastIndexOf(lastDigit) + 1);
}

export const formatPhoneTable = (col) => col.number.replace(/^\+?7?(\d{3})(\d{3})(\d{2})(\d{2})$/, '+7 $1 $2-$3-$4');

export const unformatPhone = (value) => {
  if (!value) return value;
  const onlyNumbers = value.replace(/\D/ig, '');
  return onlyNumbers.slice(onlyNumbers.length - 10, onlyNumbers.length);
};

export function simplifyLogin(login) {
  const cleanCtn = login.replace(/[^\w\d]/ig, '');

  if (/^9\d{9}$/.test(cleanCtn)) {
    return cleanCtn;
  }
  if (/^[7,8]9\d{9}$/.test(cleanCtn)) {
    return cleanCtn.slice(1, Infinity);
  }

  return cleanCtn;
}

export function formatMac(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/(..)/g, '$1:').slice(0, -1);
}

export function formatFttb(value) {
  return value.replace('FTTB/', '').split('').reduce((result, char) => result.replace('_', char), FTTB_MASK);
}

export function isCtn(value) {
  const MAX_CTN_LENGTH = 10;
  const FIRST_CTN_DIGIT = 9;

  return value.length === MAX_CTN_LENGTH
         && parseInt(value[0], 10) === FIRST_CTN_DIGIT;
}

export function formatLogin(value = '', isFttb = false) {
  const login = value.toString();

  if (isFttb) {
    return formatFttb(login);
  }

  return isCtn(login) ? formatPhone(login) : login;
}

export const cleanupPhone = (value) => value.split('')
  .reduce((result, char, index) => `${result}${PHONE_MASK[index] === '_' ? char : ''}`, '');

export function formatCurrency(input) {
  return input
    .replace(/(\d+)\sUSD/, '$$$1')
    .replace(/(\d+)\sдол/, '$$$1')
    .replace(/(\d+)\sRUR/, '$1 ₽')
    .replace('руб.', '₽');
}

/**
 * @desc: возвращает число в виде строки отформатированной как валюта
 * @param { Number } num
 * @return {string}
 */
export const getLocalCurrencyStr = (num) => {
  try {
    if (Number.isNaN(num)) {
      throw new TypeError('Failed argument type: type of num argument is invalid, it must be a Number');
    }

    return num.toLocaleString('ru-RU', {
      style: 'currency', currency: 'rub',
    }).replace(',00', '');
  } catch (e) {
    return undefined;
  }
};

export function formatDate(dateStr) {
  if (!dateStr) return '';
  const months = 'января февраля марта апреля мая июня июля августа сентября октября ноября декабря'
    .split(' ');
  const today = new Date();
  const date = new Date(dateStr);
  return date.getYear() === today.getYear()
    ? `${date.getDate()}\u00a0${months[date.getMonth()]}`
    : `${date.getDate()}\u00a0${months[date.getMonth()]}\u00a0${date.getFullYear()}`;
}

export function parseDateString(date) {
  if (typeof date === 'string') {
    // DD.MM.YYYY
    const shortDateMatch = date.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);

    if (shortDateMatch) {
      const [, DD, MM, YYYY] = shortDateMatch;

      return parse(`${YYYY}-${MM}-${DD}`);
    }

    // DD.MM.YYYY HH:mm
    const longDateMatch = date.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/);

    if (longDateMatch) {
      const [, DD, MM, YYYY, hh, mm] = longDateMatch;

      return parse(`${YYYY}-${MM}-${DD} ${hh}:${mm}`);
    }
  }

  return parse(date);
}

export function formatDateString(date, formatString) {
  return format(parseDateString(date), formatString, {
    locale: ruLocale,
  });
}

// Функция возвращает: Сегодня, Вчера, 11 сентября
export function formatTodayYesterday(jsDate) {
  if (isToday(jsDate)) return 'Сегодня';
  if (isYesterday(jsDate)) return 'Вчера';
  return formatDate(jsDate.toJSON());
}

export function formatPrice(value) {
  if (!value) return 0;
  const number = typeof value === 'number' ? value : Number(value.replace(',', '.'));
  return Number(number.toFixed(2)).toString().replace('.', ',');
}

export function formatUnitString(value, unit) {
  switch (unit) {
    case 'SECONDS': {
      const formattedSeconds = parseInt(value, 10);

      if (formattedSeconds === 0) {
        return '0 сек.';
      }
      const minutes = Math.floor(formattedSeconds / 60);
      const seconds = formattedSeconds % 60;
      let toReturn = '';
      if (minutes) {
        toReturn += `${minutes} мин.`;
      }
      if (minutes && seconds) {
        toReturn += ' ';
      }
      if (seconds) {
        toReturn += `${seconds} сек.`;
      }
      return toReturn;
    }
    case 'KBYTE':
      if (value < 1024) return `${value} КБ`;
      return value < 1048576
        ? `${(value / 1024).toFixed(2)} МБ`
        : `${(value / 1048576).toFixed(2)} ГБ`;
    case 'PIECE':
    case 'SMS':
    case 'MMS':
      return `${value} шт.`;
    case 'RUR':
      return `${formatPrice(value)} ₽`;
    case 'USD':
      return `$${formatPrice(value)}`;
    case 'CURRENCY':
      return `${value} ₽`;
    default:
      return `${value} ${unit}.`;
  }
}

export const getPaySumString = (minAmount, maxAmount) => ((!minAmount || !maxAmount)
  ? 'Введите сумму'
  : `От ${minAmount} до ${maxAmount} ₽`);

export const templateParser = (string, vars) => {
  if (!string) return '';
  if (!vars) return string;
  return string.replace(/{{(\w+)}}/ig, (full, match) => vars[match]);
};
